/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, SetStateAction, useState } from 'react';
import { toast } from 'sonner';

import type { AgentSummary } from '@/hooks/useAIAgents';
import { useAutoDrawerStore } from '@/sections/insights/aiAssistant/stores/autoDrawer.store';
import type { HannibalLaunchConfig } from '@/sections/insights/aiAssistant/types/hunt.types';
import { is402 } from '@/sections/insights/aiAssistant/utils/budgetGate';
import { localDateTimeInputToISOString } from '@/sections/insights/hunts/dateTimeLocal';
import { isHuntRootAgent } from '@/sections/insights/hunts/huntForm';

interface UseHuntModeProps {
  setNewMessage: Dispatch<SetStateAction<string>>;
  agents: AgentSummary[] | undefined;
  createHunt: (
    params: {
      prompt: string;
      expiresAt: string;
      finishCriteria?: string;
      agent?: string;
      allowedTools?: string[];
      scope?: string[];
      aggressiveness?: HannibalLaunchConfig['aggressiveness'];
    },
    options: {
      onSuccess: (hunt: any) => void;
      onError?: (err: unknown) => void;
    }
  ) => void;
  isHuntPending: boolean;
  /**
   * Called when the final launch is rejected by the backend with HTTP 402
   * (budget reached). AIAssistant uses this to surface the budget modal.
   * The wizard is already closed by the time this fires (see onError below)
   * so the budget modal is the only dialog visible.
   */
  onBudgetExhausted?: () => void;
}

export function useHuntMode({
  setNewMessage,
  agents,
  createHunt,
  isHuntPending,
  onBudgetExhausted,
}: UseHuntModeProps) {
  const [pendingMission, setPendingMission] = useState<string | null>(null);

  const huntAgents = (agents ?? []).filter(isHuntRootAgent);

  const handleHannibalLaunch = (config: HannibalLaunchConfig) => {
    createHunt(
      {
        prompt: config.mandate,
        expiresAt: localDateTimeInputToISOString(config.expiresAt),
        finishCriteria: config.finishCriteria || undefined,
        agent: config.agent,
        allowedTools: undefined,
        scope: config.scope.length > 0 ? config.scope : undefined,
        aggressiveness: config.aggressiveness,
      },
      {
        onSuccess: (hunt: any) => {
          useAutoDrawerStore.getState().open(hunt.uuid);
          setPendingMission(null);
        },
        onError: (err: unknown) => {
          // On a 402 (budget reached) close the launch wizard FIRST so only
          // the budget modal is visible (avoid two stacked dialogs), then let
          // AIAssistant open the budget modal. Non-402 failures surface a
          // toast (so they're never silent) and leave the wizard OPEN so the
          // user can retry or cancel.
          if (is402(err)) {
            setPendingMission(null);
            onBudgetExhausted?.();
            return;
          }
          // Lead with the human-readable title (the transform stores it on
          // `err.name`); the support/detail line goes in `description`,
          // matching the established sonner `toast.error(title, { description })`
          // pattern used across settings forms.
          const title =
            err instanceof Error && err.name && err.name !== 'Error'
              ? err.name
              : 'Failed to launch hunt';
          toast.error(title, {
            description: err instanceof Error ? err.message : undefined,
          });
        },
      }
    );
  };

  const handleHannibalCancel = () => {
    setNewMessage(pendingMission ?? '');
    setPendingMission(null);
  };

  return {
    pendingMission,
    setPendingMission,
    handleHannibalLaunch,
    handleHannibalCancel,
    isHuntPending,
    huntAgents,
  };
}
